import {useEffect, useState} from 'react';
import {generatePath, useNavigate} from 'react-router-dom';
import {TOSS_CLIENT_KEY, TOSS_ONETIME_CLIENT_KEY} from '@/constants';
import {PaymentActionType, PaymentProcess} from '@/enums';
import {PaymentsTranscation, ProductWithPriceInfo} from '@/interface';
import {buildSearchParam, paths} from '@/router';
import {loadTossPayments, TossPaymentsWidgets} from '@tosspayments/tosspayments-sdk';
import {useSession} from './session';

const buildSuccessSearchParams = (
  product?: ProductWithPriceInfo,
  extraParams?: {[key: string]: any},
  excludeKey?: string[]
) => {
  if (!product) {
    return '';
  }

  const params: {[key: string]: any} = {
    tier: product.tier,
    name: product.name,
    amount: product.priceInfo.finalPrice,
    discount: product.priceInfo.discountRate
      ? `${product.priceInfo.discountRate}%`
      : undefined,
    ...(extraParams || {}),
  };

  excludeKey?.forEach((key) => {
    delete params[key];
  });

  const successSearchParam = buildSearchParam(params);

  return `?${successSearchParam}`;
};

export const useTossRequestBillingAuth = (
  customerKey?: string,
  actionType?: PaymentActionType
) => {
  const {user} = useSession();
  const navigate = useNavigate();

  const loadToss = async (
    data: PaymentsTranscation,
    product?: ProductWithPriceInfo
  ) => {
    if (!customerKey || !TOSS_CLIENT_KEY) {
      return;
    }

    const tossPayments = await loadTossPayments(TOSS_CLIENT_KEY);
    const payment = tossPayments.payment({customerKey});
    const transaction = data.id;

    const hostname = window.location.origin;

    payment
      .requestBillingAuth({
        method: 'CARD',
        customerEmail: user?.email,
        successUrl:
          hostname +
          generatePath(paths.payments_process, {
            transaction,
            status: PaymentProcess.inprogress,
            type: actionType,
          }) +
          buildSuccessSearchParams(product),
        failUrl:
          hostname +
          generatePath(paths.payments_process, {
            transaction,
            status: PaymentProcess.fail,
            type: actionType,
          }),
      })
      .catch((e) => {
        if (e?.code !== 'USER_CANCEL') {
          navigate(
            {
              pathname: generatePath(paths.payments_process, {
                transaction,
                status: PaymentProcess.fail,
                type: actionType,
              }),
              search: `message=${e?.message}&code=${
                e?.code ?? 'TOSS_REQ_BILLING_AUTH_FAIL'
              }`,
            },
            {replace: true}
          );
        }
      });
  };

  return loadToss;
};

export const useTossPayWidgets = (customerKey?: string) => {
  const {user} = useSession();
  const [widgets, setWidgets] = useState<TossPaymentsWidgets>();

  const loadToss = async () => {
    if (!customerKey || !TOSS_ONETIME_CLIENT_KEY) {
      return;
    }

    const tossPayments = await loadTossPayments(TOSS_ONETIME_CLIENT_KEY);
    const tossWidgets = tossPayments.widgets({
      customerKey,
    });

    setWidgets(tossWidgets);
  };

  const requestPayment = async (
    data: PaymentsTranscation,
    product: ProductWithPriceInfo
  ) => {
    if (!widgets) {
      return;
    }

    const hostname = window.location.origin;
    const orderId = data.id;

    await widgets.requestPayment({
      orderId,
      orderName: product.name,
      customerEmail: user?.email,
      customerMobilePhone: user?.cellPhone,
      customerName: user?.nickname,
      successUrl:
        hostname +
        generatePath(paths.payments_process, {
          status: PaymentProcess.inprogress,
          transaction: orderId,
          type: PaymentActionType.onetime,
        }) +
        buildSuccessSearchParams(product, {customerKey}, ['amount']),
      failUrl:
        hostname +
        generatePath(paths.payments_process, {
          status: PaymentProcess.fail,
          transaction: orderId,
        }),
    });
  };

  useEffect(() => {
    if (customerKey) {
      loadToss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerKey]);

  return {
    widgets,
    requestPayment,
  };
};
